import { useState, useEffect, useRef, useCallback, useMemo } from "react";

interface UseInfiniteScrollOptions {
  totalCount: number;
  pageSize: number;
  rootMargin?: string;
}

export const useInfiniteScroll = ({
  totalCount,
  pageSize,
  rootMargin = "200px",
}: UseInfiniteScrollOptions) => {
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const hasMore = visibleCount < totalCount;

  const reset = useCallback(() => {
    setVisibleCount(pageSize);
  }, [pageSize]);

  const handleLoadMore = useCallback(() => {
    if (!hasMore || isLoadingMore) return;
    setIsLoadingMore(true);
    setTimeout(() => {
      setVisibleCount((prev) => prev + pageSize);
      setIsLoadingMore(false);
    }, 600);
  }, [hasMore, isLoadingMore, pageSize]);

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) handleLoadMore();
      },
      { rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleLoadMore, rootMargin]);

  // Objek hasil di-memoisasi — konsumen boleh memasukkan `scroll` (atau
  // `reset`) ke dependency array tanpa loop reset-per-render.
  return useMemo(
    () => ({ visibleCount, isLoadingMore, hasMore, loadMoreRef, reset }),
    [visibleCount, isLoadingMore, hasMore, reset],
  );
};
